import { UseMutationResult, UseQueryResult, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { httpClient } from "../network";
import { toast } from "react-toastify";
import {
  createToothProblemEndpoint,
  getClientToothProblemsEndpoint,
  getToothProblemsEndpoint,
  markToothProblemAsSolvedEndpoint,
  updateToothProblemEndpoint,
} from "../endpoints/toothProblemEndpoints";
import {
  ICreateToothProblemPayload,
  IGetToothProblemsParams,
  IMarkToothProblemAsSolvedPayload,
  IToothProblemResponse,
  IUpdateToothProblemPayload,
} from "../../../types";

// Hook za kreiranje problema zuba
export const useCreateToothProblemService = (): UseMutationResult<
  IToothProblemResponse,
  unknown,
  ICreateToothProblemPayload
> => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (payload: ICreateToothProblemPayload) => {
      const response: AxiosResponse<IToothProblemResponse> = await httpClient.post(
        createToothProblemEndpoint(),
        payload
      );
      return response.data;
    },
    onSuccess: (data) => {
      toast.success(data.message || "Problem zuba je uspješno dodan.");
      queryClient.invalidateQueries({ queryKey: ["clientToothProblems"] });
      queryClient.invalidateQueries({ queryKey: ["toothProblems"] });
    },
    onError: () => toast.error("Dodavanje problema zuba nije uspjelo"),
  });
};

// Hook za ažuriranje problema zuba
export const useUpdateToothProblemService = (): UseMutationResult<
  IToothProblemResponse,
  unknown,
  { problemId: string; payload: IUpdateToothProblemPayload }
> => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ problemId, payload }) => {
      const response: AxiosResponse<IToothProblemResponse> = await httpClient.patch(
        updateToothProblemEndpoint(problemId),
        payload
      );
      return response.data;
    },
    onSuccess: (data) => {
      toast.success(data.message || "Problem zuba je uspješno ažuriran.");
      queryClient.invalidateQueries({ queryKey: ["clientToothProblems"] });
      queryClient.invalidateQueries({ queryKey: ["toothProblems"] });
    },
    onError: () => toast.error("Ažuriranje problema zuba nije uspjelo"),
  });
};

// Hook za označavanje problema zuba kao riješenog
export const useMarkToothProblemAsSolvedService = (): UseMutationResult<
  IToothProblemResponse,
  unknown,
  { problemId: string; payload: IMarkToothProblemAsSolvedPayload }
> => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ problemId, payload }) => {
      const response: AxiosResponse<IToothProblemResponse> = await httpClient.patch(
        markToothProblemAsSolvedEndpoint(problemId),
        payload
      );
      return response.data;
    },
    onSuccess: (data) => {
      toast.success(data.message || "Problem zuba je označen kao riješen.");
      queryClient.invalidateQueries({ queryKey: ["clientToothProblems"] });
      queryClient.invalidateQueries({ queryKey: ["toothProblems"] });
    },
    onError: () => toast.error("Označavanje problema kao riješenog nije uspjelo"),
  });
};

// Hook za dohvaćanje problema zuba klijenta
export const useGetClientToothProblemsService = (
  clientId: string,
  params?: IGetToothProblemsParams
): UseQueryResult<IToothProblemResponse, unknown> => {
  return useQuery({
    queryKey: ["clientToothProblems", clientId, params],
    queryFn: async () => {
      const response: AxiosResponse<IToothProblemResponse> = await httpClient.get(
        getClientToothProblemsEndpoint(clientId, params)
      );
      return response.data;
    },
    enabled: !!clientId, // Query će se izvršiti samo ako je clientId prisutan
  });
};

// Hook za dohvaćanje problema specifičnog zuba
export const useGetToothProblemsService = (
  clientId: string,
  toothNumber: number,
  onlyCurrent?: boolean
): UseQueryResult<IToothProblemResponse, unknown> => {
  return useQuery({
    queryKey: ["toothProblems", clientId, toothNumber, onlyCurrent],
    queryFn: async () => {
      const response: AxiosResponse<IToothProblemResponse> = await httpClient.get(
        getToothProblemsEndpoint(clientId, toothNumber, onlyCurrent)
      );
      return response.data;
    },
    enabled: !!clientId && toothNumber !== undefined, // Query će se izvršiti samo ako su clientId i toothNumber prisutni
  });
}; 